import axios from 'axios'

const API_BASE_URL = '/api'

const qrApi = axios.create({
  baseURL: `${API_BASE_URL}/reservations`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
qrApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor for error handling
qrApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

/**
 * Validates a QR code's data against the backend
 * POST /api/v1/reservations/validate-qr
 */
export const validateQrCode = async (
  qrCodeData: string
): Promise<{ valid: boolean; data?: any; error?: string }> => {
  try {
    const response = await qrApi.post('/validate-qr', {
      qr_code_data: qrCodeData,
    })

    const result = response.data

    if (result.success) {
      return { valid: true, data: result.data }
    }

    return { valid: false, error: result.message || 'Invalid QR code' }
  } catch (error: any) {
    return {
      valid: false,
      error: error?.response?.data?.message || error?.message || 'Failed to validate QR code',
    }
  }
}

/**
 * Marks a reservation as picked up by scanning its QR code
 * POST /api/v1/reservations/{id}/scan-qr
 */
export const scanQrCode = async (
  reservationId: string
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const response = await qrApi.post(`/${reservationId}/scan-qr`)

    const result = response.data

    if (result.success) {
      return { success: true, data: result.data }
    }

    return { success: false, error: result.message || 'Failed to scan QR code' }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to scan QR code',
    }
  }
}

/**
 * Gets reservation details by manual lookup (reservation ID)
 * GET /api/v1/reservations/{id}
 */
export const getReservationByManualLookup = async (
  reservationId: string
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const response = await qrApi.get(`/${reservationId}`)

    const result = response.data

    if (result.success) {
      return { success: true, data: result.data }
    }

    return { success: false, error: result.message || 'Reservation not found' }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to fetch reservation',
    }
  }
}
